import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";

type SelectOption = {
  value: string;
  label: string;
};

type DropdownCoords = {
  top: number;
  left: number;
  width: number;
};

type FilterDropdownFieldProps = {
  label: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  mobile?: boolean;
  className?: string;
  testId: string;
  placeholder?: string;
  menuTestId?: string;
};

const resolveSelectedLabel = (
  options: SelectOption[],
  value: string,
  placeholder: string,
): string =>
  options.find((option) => option.value === value)?.label ?? placeholder;

const estimateDesktopTriggerWidthPx = (
  label: string,
  selectedLabel: string,
): number => {
  const estimated =
    Math.ceil(label.length * 5.25) +
    Math.ceil(selectedLabel.length * 8.25) +
    52;
  return Math.min(240, Math.max(128, estimated));
};

const optionSlug = (value: string): string =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const measureDropdownCoords = (button: HTMLButtonElement): DropdownCoords => {
  const rect = button.getBoundingClientRect();
  return {
    top: rect.bottom + window.scrollY + 8,
    left: rect.left + window.scrollX,
    width: rect.width,
  };
};

const FilterDropdown = ({
  options,
  value,
  onChange,
  label,
  mobile,
  testId,
  placeholder,
  menuTestId,
}: Omit<FilterDropdownFieldProps, "className">) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<DropdownCoords | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const pendingFocusIndexRef = useRef<number | null>(null);
  const selectedIndex = options.findIndex((option) => option.value === value);
  const selectedLabel = resolveSelectedLabel(
    options,
    value,
    placeholder ?? "All",
  );

  const closeDropdown = (returnFocus = false) => {
    setIsOpen(false);
    if (returnFocus) {
      window.requestAnimationFrame(() => {
        buttonRef.current?.focus();
      });
    }
  };

  const openDropdown = (focusIndex?: number) => {
    if (!buttonRef.current) return;

    setCoords(measureDropdownCoords(buttonRef.current));

    if (typeof focusIndex === "number") {
      pendingFocusIndexRef.current = focusIndex;
    }

    setIsOpen(true);
  };

  const focusOptionByIndex = (index: number) => {
    if (options.length === 0) return;

    const clampedIndex = Math.max(0, Math.min(options.length - 1, index));
    const option = optionRefs.current[clampedIndex];
    if (!option) return;

    option.focus();
    option.scrollIntoView?.({ block: "nearest" });
  };

  const focusSelectedOrEdge = (edge: "first" | "last") => {
    if (options.length === 0) return;

    const focusIndex =
      selectedIndex >= 0
        ? selectedIndex
        : edge === "last"
          ? options.length - 1
          : 0;

    if (isOpen) {
      focusOptionByIndex(focusIndex);
      return;
    }

    openDropdown(focusIndex);
  };

  const toggleDropdown = () => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }

    openDropdown();
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const closeOnResize = () => setIsOpen(false);
    const syncOnScroll = (event: Event) => {
      const target = event.target as Node | null;
      if (target && dropdownRef.current?.contains(target)) return;
      if (target && buttonRef.current?.contains(target)) return;
      if (!buttonRef.current) {
        setIsOpen(false);
        return;
      }
      setCoords(measureDropdownCoords(buttonRef.current));
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeDropdown(true);
      }
    };
    const closeOnOutsidePress = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (buttonRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setIsOpen(false);
    };

    window.addEventListener("resize", closeOnResize);
    window.addEventListener("scroll", syncOnScroll, true);
    window.addEventListener("keydown", closeOnEscape);
    window.addEventListener("mousedown", closeOnOutsidePress);

    return () => {
      window.removeEventListener("resize", closeOnResize);
      window.removeEventListener("scroll", syncOnScroll, true);
      window.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("mousedown", closeOnOutsidePress);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || pendingFocusIndexRef.current === null) return undefined;

    const focusIndex = pendingFocusIndexRef.current;
    pendingFocusIndexRef.current = null;

    const frame = window.requestAnimationFrame(() => {
      focusOptionByIndex(focusIndex);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isOpen, options.length]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        data-testid={testId}
        data-selected={value}
        aria-label={`${label} filter`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={toggleDropdown}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            focusSelectedOrEdge("first");
            return;
          }
          if (event.key === "ArrowUp") {
            event.preventDefault();
            focusSelectedOrEdge("last");
            return;
          }
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggleDropdown();
            return;
          }
          if (event.key === "Escape" && isOpen) {
            event.preventDefault();
            closeDropdown();
          }
        }}
        className={`flex h-9 min-h-9 items-center justify-between gap-2 rounded-lg border text-[0.8rem] font-semibold leading-none whitespace-nowrap transition-colors focus:outline-none focus:ring-0 ${
          mobile
            ? "w-full border-gray-200 bg-gray-50 px-3 py-2.5 text-irongrey hover:border-primary/50"
            : "border-transparent px-3 py-1.5 text-irongrey hover:text-primary"
        }`}
        style={
          mobile
            ? undefined
            : {
                width: `${estimateDesktopTriggerWidthPx(label, selectedLabel)}px`,
              }
        }
      >
        <span className="flex min-w-0 items-baseline gap-1.5">
          <span className="shrink-0 text-[10px] font-bold uppercase tracking-tight text-irongrey/40">
            {label}
          </span>
          <span className="truncate">{selectedLabel}</span>
        </span>
        <ChevronDown
          size={14}
          className={`shrink-0 text-irongrey/40 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && coords && typeof document !== "undefined"
        ? createPortal(
            <>
              <div
                data-passive-click-surface="true"
                className="fixed inset-0 z-[9998] cursor-default"
                onClick={() => setIsOpen(false)}
              />
              <div
                ref={dropdownRef}
                data-testid={menuTestId ?? `${testId}-menu`}
                className="absolute z-[9999] rounded-xl border border-gray-100 bg-white py-2 shadow-soft animate-in fade-in zoom-in-95 duration-100"
                style={{
                  top: coords.top,
                  left: coords.left,
                  width: Math.max(coords.width, 196),
                }}
                role="listbox"
                aria-label={`${label} options`}
              >
                <div className="max-h-60 overflow-y-auto">
                  {options.map((option, index) => (
                    <button
                      key={option.value}
                      ref={(element) => {
                        optionRefs.current[index] = element;
                      }}
                      type="button"
                      role="option"
                      aria-selected={value === option.value}
                      data-testid={`${testId}-option-${optionSlug(option.value)}`}
                      className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                        value === option.value
                          ? "bg-primary/5 font-bold text-primary"
                          : "text-irongrey hover:bg-gray-50"
                      }`}
                      onKeyDown={(event) => {
                        if (event.key === "ArrowDown") {
                          event.preventDefault();
                          focusOptionByIndex(index + 1);
                          return;
                        }
                        if (event.key === "ArrowUp") {
                          event.preventDefault();
                          focusOptionByIndex(index - 1);
                          return;
                        }
                        if (event.key === "Home") {
                          event.preventDefault();
                          focusOptionByIndex(0);
                          return;
                        }
                        if (event.key === "End") {
                          event.preventDefault();
                          focusOptionByIndex(options.length - 1);
                          return;
                        }
                        if (event.key === "Escape") {
                          event.preventDefault();
                          closeDropdown(true);
                        }
                      }}
                      onClick={() => {
                        onChange(option.value);
                        setIsOpen(false);
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </>,
            document.body,
          )
        : null}
    </>
  );
};

const FilterDropdownField = ({
  label,
  value,
  options,
  onChange,
  mobile = false,
  className = "",
  testId,
  placeholder = "All",
  menuTestId,
}: FilterDropdownFieldProps) => (
  <div
    className={`${mobile ? "flex min-w-[130px] flex-1" : "flex min-w-0 items-center"} ${className}`}
  >
    <FilterDropdown
      options={options}
      value={value}
      onChange={onChange}
      label={label}
      mobile={mobile}
      testId={testId}
      placeholder={placeholder}
      menuTestId={menuTestId}
    />
  </div>
);

const FilterBarSection = ({
  children,
  mobile = false,
  className = "",
  testId,
}: {
  children: React.ReactNode;
  mobile?: boolean;
  className?: string;
  testId?: string;
}) => (
  <section
    data-testid={testId}
    className={`${mobile ? "grid w-full grid-cols-2 gap-3 pt-2" : "flex items-center gap-0"} ${className}`}
  >
    {children}
  </section>
);

const toSelectOptions = (options: string[]): SelectOption[] =>
  options.map((option) => ({ value: option, label: option }));

export { FilterBarSection, FilterDropdownField, toSelectOptions };
export type { SelectOption };
